/**
 * Persisted entities and their write schemas.
 *
 * Money is carried as integer minor units throughout. Ratios are fractions.
 * Probabilities are basis points (0–10000) because the source model typed them as
 * integers against float stage values, which silently zeroed every weighted
 * forecast — see normalisation N3.
 */
import { z } from 'zod';
import { CATEGORIES, CLIENT_STATUSES, CURRENCIES, GRADES, INVOICING_ENTITIES, ITEM_TYPES, LINE_FREQUENCIES, LOADING_WINDOWS, PIPELINE_STAGES, QUOTE_STATUSES, RELEASE_STATUSES, ROLES, } from './enums.js';
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected an ISO date, YYYY-MM-DD');
export const ClientSchema = z.object({
    id: z.string().uuid(),
    clientCode: z.string().length(3).regex(/^[A-Z]{3}$/, 'Client code is three upper-case letters'),
    legalName: z.string().min(1),
    tradingName: z.string().nullable(),
    industry: z.string().nullable(),
    regulators: z.array(z.string()).default([]),
    status: z.enum(CLIENT_STATUSES),
    ownerUserId: z.string().nullable(),
    invoicingEntity: z.enum(INVOICING_ENTITIES),
    defaultCurrency: z.enum(CURRENCIES).default('HKD'),
    folderPath: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const ClientCreateSchema = ClientSchema.omit({ id: true, createdAt: true, updatedAt: true }).extend({
    clientCode: z.string().length(3).regex(/^[A-Za-z]{3}$/).transform((s) => s.toUpperCase()),
});
export const SiteSchema = z.object({
    id: z.string().uuid(),
    clientId: z.string().uuid(),
    siteName: z.string().min(1),
    country: z.string().min(1),
    city: z.string().nullable(),
    address: z.string().nullable(),
    headcount: z.number().int().nonnegative().default(0),
    desks: z.number().int().nonnegative().default(0),
    floorAreaSqm: z.number().nonnegative().nullable(),
    isPrimary: z.boolean().default(false),
    supportModel: z.string().nullable(),
});
export const ContactSchema = z.object({
    id: z.string().uuid(),
    clientId: z.string().uuid(),
    name: z.string().min(1),
    role: z.string().nullable(),
    email: z.string().email().nullable(),
    phone: z.string().nullable(),
    isCommercial: z.boolean().default(false),
    isTechnical: z.boolean().default(false),
    isDecisionMaker: z.boolean().default(false),
});
/**
 * Environment profiles are versioned so an old quote still reproduces against the
 * environment it was priced from.
 */
export const EnvironmentVersionSchema = z.object({
    id: z.string().uuid(),
    clientId: z.string().uuid(),
    versionNumber: z.number().int().positive(),
    answers: z.record(z.unknown()),
    capturedBy: z.string().nullable(),
    capturedAt: z.string(),
    note: z.string().nullable(),
});
export const OpportunitySchema = z.object({
    id: z.string().uuid(),
    clientId: z.string().uuid(),
    reference: z.string(),
    title: z.string().min(1),
    stage: z.enum(PIPELINE_STAGES),
    /** Basis points. 0.05 is stored as 500. */
    probabilityBp: z.number().int().min(0).max(10000),
    ownerUserId: z.string().nullable(),
    /** The routed primary category, plus any modules that compose onto it. */
    category: z.enum(CATEGORIES).nullable(),
    modules: z.array(z.enum(CATEGORIES)).default([]),
    serviceLine: z.string().nullable(),
    expectedValueBasis: z.enum(['one_off', 'annual_recurring', 'contract_value']).default('contract_value'),
    expectedValue: z.number().int().nonnegative().default(0),
    clientDeadline: isoDate.nullable(),
    source: z.string().nullable(),
    whatPromptedThis: z.string().nullable(),
    successLooksLike: z.string().nullable(),
    closedLostReason: z.string().nullable(),
    closedLostCompetitor: z.string().nullable(),
    closedLostWinningPrice: z.number().int().nullable(),
    closedWonReference: z.string().nullable(),
    contractStartDate: isoDate.nullable(),
    contractEndDate: isoDate.nullable(),
    confirmedValue: z.number().int().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const ActivitySchema = z.object({
    id: z.string().uuid(),
    opportunityId: z.string().uuid().nullable(),
    clientId: z.string().uuid().nullable(),
    kind: z.enum(['note', 'call', 'meeting', 'email', 'task', 'stage_change', 'system']),
    summary: z.string().min(1),
    detail: z.string().nullable(),
    dueDate: isoDate.nullable(),
    completedAt: z.string().nullable(),
    actorUserId: z.string().nullable(),
    createdAt: z.string(),
});
export const QuoteSchema = z.object({
    id: z.string().uuid(),
    opportunityId: z.string().uuid(),
    clientId: z.string().uuid(),
    reference: z.string(),
    currentVersionId: z.string().uuid().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const QuoteVersionSchema = z.object({
    id: z.string().uuid(),
    quoteId: z.string().uuid(),
    versionLabel: z.string(),
    status: z.enum(QUOTE_STATUSES),
    category: z.enum(CATEGORIES),
    currency: z.enum(CURRENCIES),
    fxRate: z.number().positive().default(1),
    fxRateDate: isoDate.nullable(),
    referenceReleaseId: z.string(),
    policyFingerprint: z.string(),
    engineVersion: z.string(),
    /** Canonical hash of the priced content. Approval binds to this, not to the row id. */
    contentHash: z.string(),
    inputs: z.record(z.unknown()),
    result: z.record(z.unknown()),
    contractTermMonths: z.number().int().positive().default(12),
    validityDays: z.number().int().positive().default(30),
    paymentTerms: z.string().nullable(),
    preparedByUserId: z.string().nullable(),
    issuedAt: z.string().nullable(),
    frozenAt: z.string().nullable(),
    createdAt: z.string(),
});
export const ApprovalRequestSchema = z.object({
    id: z.string().uuid(),
    quoteVersionId: z.string().uuid(),
    contentHash: z.string(),
    triggers: z.array(z.object({ code: z.string(), reason: z.string(), rule: z.string() })),
    requestedByUserId: z.string().nullable(),
    requestedAt: z.string(),
    status: z.enum(['pending', 'approved', 'returned', 'invalidated']),
});
export const ApprovalDecisionSchema = z.object({
    id: z.string().uuid(),
    approvalRequestId: z.string().uuid(),
    decision: z.enum(['approved', 'returned']),
    comments: z.string().nullable(),
    decidedByUserId: z.string().nullable(),
    decidedAt: z.string(),
    /** Recorded again so a decision can be proved to bind the content it saw. */
    contentHash: z.string(),
});
export const ReferenceReleaseSchema = z.object({
    id: z.string(),
    label: z.string(),
    status: z.enum(RELEASE_STATUSES),
    effectiveFrom: isoDate.nullable(),
    preparedBy: z.string().nullable(),
    approvedBy: z.string().nullable(),
    publishedAt: z.string().nullable(),
    notes: z.string().nullable(),
    createdAt: z.string(),
});
export const ProductSchema = z.object({
    id: z.string().uuid(),
    releaseId: z.string(),
    sku: z.string().min(1),
    vendor: z.string().min(1),
    name: z.string().min(1),
    itemType: z.enum(ITEM_TYPES),
    billingUnit: z.string().min(1),
    frequency: z.enum(LINE_FREQUENCIES),
    currency: z.enum(CURRENCIES).default('HKD'),
    costPrice: z.number().int().nonnegative().nullable(),
    sellPrice: z.number().int().nonnegative().nullable(),
    termMonths: z.number().int().positive().nullable(),
    effectiveFrom: isoDate.nullable(),
    taxCategory: z.string().nullable(),
    sourceReference: z.string().nullable(),
    approved: z.boolean().default(false),
    notes: z.string().nullable(),
});
export const ProductWriteSchema = ProductSchema.omit({ id: true }).partial({ releaseId: true });
export const LabourRateSchema = z.object({
    id: z.string().uuid(),
    releaseId: z.string(),
    grade: z.enum(GRADES),
    gradeName: z.string(),
    rateType: z.enum(['daily', 'hourly']),
    currency: z.enum(CURRENCIES).default('HKD'),
    sellRate: z.number().int().nonnegative().nullable(),
    internalCost: z.number().int().nonnegative().nullable(),
    costIsPlaceholder: z.boolean().default(true),
    /** True where the value equals day ÷ 8. Imported as stated, never computed. */
    derivedFromDay: z.boolean().default(false),
    effectiveFrom: isoDate.nullable(),
    sourceReference: z.string().nullable(),
});
export const LoadingSchema = z.object({
    id: z.string().uuid(),
    releaseId: z.string(),
    window: z.enum(LOADING_WINDOWS),
    label: z.string(),
    multiplier: z.number().positive().nullable(),
    minimumHours: z.number().nonnegative().nullable(),
    definition: z.string().nullable(),
    effectiveFrom: isoDate.nullable(),
});
export const AuditEventSchema = z.object({
    id: z.string().uuid(),
    actorUserId: z.string().nullable(),
    action: z.string(),
    entityType: z.string(),
    entityId: z.string(),
    before: z.unknown().nullable(),
    after: z.unknown().nullable(),
    requestId: z.string().nullable(),
    createdAt: z.string(),
});
export const UserSchema = z.object({
    id: z.string(),
    subject: z.string(),
    issuer: z.string(),
    displayName: z.string(),
    email: z.string().nullable(),
    roles: z.array(z.enum(ROLES)),
    active: z.boolean().default(true),
});
export const DocumentSchema = z.object({
    id: z.string().uuid(),
    quoteVersionId: z.string().uuid(),
    kind: z.enum(['proposal', 'quotation_letter', 'terms', 'pricing_schedule', 'calculation_evidence', 'scope_of_services']),
    audience: z.enum(['client', 'internal', 'vendor']),
    filename: z.string(),
    contentHash: z.string(),
    templateVersion: z.string(),
    body: z.string(),
    createdAt: z.string(),
});
//# sourceMappingURL=entities.js.map